import {
  apiGet, apiPost,
} from '~/_shared/utils/api/api.helpers';
import type {
  Place, PlaceId,
} from './places.types';

export enum PlacesSearchErrorCode {
  BAD_REQUEST = 'BAD_REQUEST',
  FAILED = 'FAILED',
  LIMIT_REACHED = 'LIMIT_REACHED',
  SERVER_ERROR = 'SERVER_ERROR',
}

export enum PlacesSearchResultsRankingPreference {
  Distance = 'distance',
  Relevance = 'relevance',
}

export enum PlacesSearchProvider {
  Google = 'google',
  Foursquare = 'foursquare',
}

export type PlacesSearchRectangle = Readonly<{
  low: Readonly<{
    latitude: number;
    longitude: number;
  }>;
  high: Readonly<{
    latitude: number;
    longitude: number;
  }>;
}>;

export type PlacesSearchRadius = Readonly<{
  latitude: number;
  longitude: number;
  radius: number;
}>;

// only the Foursquare provider accepts the radius variant
export type PlacesSearchLocation =
  | Readonly<{ rectangle: PlacesSearchRectangle }>
  | Readonly<{ radius: PlacesSearchRadius }>;

export type GetPlacesByTextSearchRequestItem = Readonly<{
  text: string;
  location: PlacesSearchLocation;
  preference: PlacesSearchResultsRankingPreference;
  more?: boolean;
}>;

export type GetPlacesByTextSearchRequest = Readonly<{
  params: ReadonlyArray<GetPlacesByTextSearchRequestItem>;
}>;

export type GetPlacesByTextSearchResponse = Readonly<{
  data: ReadonlyArray<Readonly<{
    google_places_requests_usage: number; // intentionally uses one limit 'google_places_requests_usage' for all providers for now
    items?: ReadonlyArray<Place>;
    hasMore?: boolean;
    code?: PlacesSearchErrorCode;
  }>>;
}>;

export type PlaceDetailResponse = Readonly<{
  code?: 'NOT_FOUND' | 'SERVER_ERROR'; // not sent on success
  data?: PlaceDetail;
}>;

export type PlaceDetail = Readonly<{
  // always present
  id: string | null;
  name: string | null;
  address: string | null;
  location: { lat: number | null; lng: number | null };

  // present only when the provider has a value
  addressComponents?: {
    street?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  };
  categories?: string[];
  phone?: string;
  website?: string;
  email?: string; // Foursquare only
  socialMedia?: { // Foursquare only
    facebook?: string; // numeric page id, not a URL
    twitter?: string; // handle, not a URL
    instagram?: string;
  };
  description?: string;
  photos?: PlacePhoto[]; // Foursquare only (Premium)
  rating?: { value: number; max: number }; // max differs: FSQ 10, Google 5
  priceLevel?: number; // 1–4
  popularity?: number; // 0–1, Foursquare only
  hours?: {
    display?: string;
    openNow?: boolean;
    regular?: OpeningInterval[];
  };
  popularHours?: OpeningInterval[];
  tips?: { text: string; createdAt?: string }[];
  tastes?: string[];
  attributes?: Record<string, boolean | string>; // e.g. { delivery: false }
  stats?: { totalPhotos?: number; totalRatings?: number; totalTips?: number };
  chains?: { id?: string; name: string }[];
  storeId?: string;
  distance?: number; // metres, from the search that cached it
  menu?: string;
  link?: string; // absolute provider URL
  businessStatus?: string; // "OPERATIONAL" | "CLOSED_PERMANENTLY" | …
  dateClosed?: string; // YYYY-MM-DD
}>;

export type PlacePhoto = Readonly<{
  url: string; // original size
  thumbnail: string; // ≤400px longest edge, aspect preserved
  width?: number;
  height?: number;
  classifications?: string[]; // e.g. ["outdoor", "logos"]
  createdAt?: string;
}>;

type OpeningInterval = Readonly<{
  day: number; // 1 = Monday … 7 = Sunday
  open?: string; // "1100"
  close?: string; // "2000"
}>;

export const getGooglePlacesByTextSearch = (
  clientId: number,
  params: GetPlacesByTextSearchRequest,
  mapId?: number,
): Promise<GetPlacesByTextSearchResponse> => {
  const requestUrl = `/api/clients/${clientId}/places/google/text-search-new${mapId ? `?map_id=${mapId}` : ''}`;
  return apiPost(requestUrl, params);
};

export const getFoursquarePlacesByTextSearch = (
  clientId: number,
  params: GetPlacesByTextSearchRequest,
  mapId?: number,
): Promise<GetPlacesByTextSearchResponse> => {
  const requestUrl = `/api/clients/${clientId}/places/foursquare/place-search${mapId ? `?map_id=${mapId}` : ''}`;
  return apiPost(requestUrl, params);
};

export const getPlacesByTextSearch = (provider: PlacesSearchProvider) => (
  provider === PlacesSearchProvider.Foursquare
    ? getFoursquarePlacesByTextSearch
    : getGooglePlacesByTextSearch
);

export const getPlaceDetails = (
  clientId: number,
  placeId: PlaceId,
  provider: PlacesSearchProvider,
  mapId?: number,
): Promise<PlaceDetailResponse> => {
  const requestUrl = `/api/clients/${clientId}/places/${provider}/place/${placeId}${mapId ? `?map_id=${mapId}` : ''}`;
  return apiGet(requestUrl);
};
